import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

// Components
import PhoneNumbers from "../../components/PhoneNumbers.js";
import Marquee from "../general/Marquee.js";

// Style
import {z} from "../../style/components/mixins.js";
import {bp} from "../../style/components/breakpoints.js";
import {voice} from "../../style/components/typography";
import {border, pad, radius} from "../../style/components/variables";
import {Abbr, AnchorInline, HeadingSmall, Text} from "../../style/components/general.js";

const Contacts = ({details, control, fullWidth = false}) => {
  const {contactsExtra} = details;
  const [contacts, setContacts] = useState({});

  useEffect(() => {
    const grouped = {};
    contactsExtra?.forEach(contact => {
      const department = contact.department?.name || "Contact";
      const company = contact.company?.name || "No Company Provided";
      const formatted = {
        name: `${contact.firstName} ${contact.lastName}`,
        primary: contact.primary,
        phones: contact.phones,
        email: contact.email
      };

      if (!grouped[department]) grouped[department] = {[company]: [formatted]};
      else if (!grouped[department][company]) grouped[department][company] = [formatted];
      else grouped[department][company].push(formatted);
    });
    setContacts(grouped);
  }, [contactsExtra]);

  return (
    <Wrapper>
      {control}
      {Object.keys(contacts).length ? (
        <>
          <CardHeading>Contacts</CardHeading>
          {Object.keys(contacts).map(department => (
            <Detail key={department} fullWidth={fullWidth}>
              <DetailLabel fullWidth={fullWidth}>
                <Abbr title={department}>{department}</Abbr>
              </DetailLabel>
              <Notes fullWidth={fullWidth}>
                {Object.keys(contacts[department]).map(company => (
                  <div key={company}>
                    <Marquee text={company} />
                    {contacts[department][company].map(contact => (
                      <div key={contact.email}>
                        <NoteText quiet>
                          <Abbr title={contact.name}>{contact.name}</Abbr>
                        </NoteText>
                        {contact.primary && contact.phones?.length > 0 && (
                          <PhoneNumbers primary={contact.primary} phones={contact.phones} />
                        )}
                        <NoteLink href={`mailto:${contact.email}`}>
                          <Abbr title={contact.email}>{contact.email}</Abbr>
                        </NoteLink>
                      </div>
                    ))}
                  </div>
                ))}
              </Notes>
            </Detail>
          ))}
        </>
      ) : (
        <NoContacts>
          <Text>No Contacts</Text>
        </NoContacts>
      )}
    </Wrapper>
  );
};

Contacts.propTypes = {
  details: PropTypes.objectOf(PropTypes.any).isRequired,
  control: PropTypes.node,
  fullWidth: PropTypes.bool
};

// Style Overrides
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 330px;
  padding: ${pad}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  margin-bottom: ${pad}px;

  ${bp(3)} {
    min-width: 100%;
  }
`;

const CardHeading = styled(HeadingSmall)`
  ${voice.weak};
`;

const Detail = styled.div`
  display: flex;
  justify-content: space-between;
  margin: ${pad}px 0;

  &:last-child {
    margin-bottom: 0;
  }

  ${({fullWidth}) =>
    fullWidth &&
    css`
      flex-direction: column;
    `}
`;

const DetailLabel = styled(Text)`
  width: 40%;
  padding-right: ${pad}px;
  text-overflow: ellipsis;
  overflow: hidden;

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
      padding: 0;
      overflow: inherit;
    `}
`;

const Notes = styled.div`
  width: 60%;

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const NoteText = styled(Text)`
  display: block;
  color: ${({theme}) => theme.secondary};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const NoteLink = styled(AnchorInline)`
  ${voice.quiet};
  display: block;
  color: ${({theme}) => theme.primary};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-bottom: ${pad / 2}px;
  position: relative;
  z-index: ${z("above")};
`;

const NoContacts = styled.div`
  display: flex;
  justify-content: center;
  margin: ${pad}px 0;
`;

export default Contacts;
